import { useEffect, useState, useCallback } from 'react';
import { useApiClient } from './useApiClient';

/**
 * Persisted Station-6 (Ontology Inference) results, as served by
 * GET /api/ontology-inference/results/:job_id — the same endpoint the legacy
 * results page reads, so any summary derived here always agrees with the deep
 * view. Shapes mirror server/db/semanticInteropRepository.ts.
 */
export interface OntologyMappingRecord {
  column_name: string;
  column_index: number;
  ontology_code: string;
  ontology_name: string;
  ontology_description?: string | null;
  ontology_id?: string | null;
  ontology_iri?: string | null;
  confidence: number;
  match_method?: string | null;
  status?: string;
}

export interface UnmappedColumnRecord {
  column_name: string;
  column_index: number;
  reason?: string | null;
  suggestions?: unknown;
}

/** Older rows persisted plain strings; newer rows persist descriptors. */
export type OntologySearched =
  | string
  | { code: string; name: string; description?: string; terms_checked?: number; source?: string };

export interface OntologyInferenceResults {
  job_id: string;
  status: string;
  result: {
    total_columns: number;
    mapped_columns: number;
    unmapped_columns: number;
    mappings: OntologyMappingRecord[];
    unmapped: UnmappedColumnRecord[];
    dataset_path?: string;
    ontologies_searched: OntologySearched[];
  };
}

export function useOntologyInferenceResults(jobId: string | undefined) {
  const { authFetch } = useApiClient();
  const [data, setData] = useState<OntologyInferenceResults | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async () => {
    if (!jobId) return;
    setLoading(true);
    setError(null);
    try {
      const r = await authFetch(`/api/ontology-inference/results/${jobId}`);
      if (!r.ok) {
        const errText = await r.text();
        throw new Error(`HTTP ${r.status}: ${errText}`);
      }
      setData(await r.json() as OntologyInferenceResults);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [jobId, authFetch]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  return { data, loading, error, refetch };
}
